import { ErrorMessage, Input } from "apps/portal/src/components/atoms";
import { Link04Icon } from "apps/portal/src/components/atoms/icons/Link04Icon";
import { useTranslation } from "react-i18next";

import { roundToTwoDecimals } from "./utils/roundToTwoDecimals";

export type PriceValueType = {
  exTaxPrice: number;
  incTaxPrice: number;
  salesTaxRate: number;
};

type PriceBlockProps = {
  value: PriceValueType;
  onChange: (value: PriceValueType) => void;
  customLabel?: string;
  exTaxError?: string;
  incTaxError?: string;
  taxRateError?: string;
};

export const PriceBlock = ({
  value,
  onChange,
  customLabel,
  exTaxError,
  incTaxError,
  taxRateError,
}: PriceBlockProps) => {
  const { t } = useTranslation();

  const { exTaxPrice, incTaxPrice, salesTaxRate } = value;

  const handleExTaxChange = (newEx: number) => {
    const newInc = roundToTwoDecimals(newEx + (newEx * salesTaxRate) / 100);
    onChange({ ...value, exTaxPrice: newEx, incTaxPrice: newInc });
  };

  const handleIncTaxChange = (newInc: number) => {
    const newEx = roundToTwoDecimals(newInc / (1 + salesTaxRate / 100));
    onChange({ ...value, incTaxPrice: newInc, exTaxPrice: newEx });
  };

  const handleTaxRateChange = (newRate: number) => {
    const newInc = roundToTwoDecimals(
      exTaxPrice + (exTaxPrice * newRate) / 100,
    );
    onChange({ ...value, salesTaxRate: newRate, incTaxPrice: newInc });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-sm font-medium text-gray-950">
        {customLabel ?? t("components.priceBlock.title")}
      </div>
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-1 flex-row gap-1">
          <div className="flex flex-1 flex-col gap-1.5">
            <Input
              value={exTaxPrice.toString()}
              onChange={(e) => {
                const val = parseFloat(e.target.value || "0");
                handleExTaxChange(val);
              }}
              type="number"
              isRequired
              label={t("components.priceBlock.exTaxPrice.label")}
            />
            {exTaxError && <ErrorMessage text={exTaxError} />}
          </div>
          <div className="mt-9">
            <Link04Icon className="size-5" />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Input
              value={incTaxPrice.toString()}
              onChange={(e) => {
                const val = parseFloat(e.target.value || "0");
                handleIncTaxChange(val);
              }}
              type="number"
              isRequired
              label={t("components.priceBlock.incTaxPrice.label")}
            />
            {incTaxError && <ErrorMessage text={incTaxError} />}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-1.5">
          <Input
            value={salesTaxRate.toString()}
            onChange={(e) => {
              const val = parseFloat(e.target.value || "0");
              handleTaxRateChange(val);
            }}
            type="number"
            label={t("components.priceBlock.salesTaxRate.label")}
            endIcon={
              <div className="text-base font-normal text-gray-500">%</div>
            }
            isRequired
          />
          {taxRateError && <ErrorMessage text={taxRateError} />}
        </div>
      </div>
    </div>
  );
};
